/**
 * Saved Predictions – via Inference API (GET/POST/PATCH/DELETE /api/v1/me/saved-predictions).
 * Tier cap enforced server-side (buyer_3month: 20, pro_monthly: 100).
 */

import { getAccessToken } from './auth'

import { API_BASE_URL } from '../config'

function authHeaders() {
  const token = getAccessToken()
  const h = { 'Content-Type': 'application/json' }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

/**
 * Get saved predictions for the current user (requires auth).
 * @param {number} [limit=100] - Max results
 * @param {number} [offset=0] - Pagination offset
 * @returns {Promise<{ predictions: Array, count: number, cap: number }>}
 */
export async function getSavedPredictions(limit = 100, offset = 0) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  const r = await fetch(`${API_BASE_URL}/api/v1/me/saved-predictions?${params}`, {
    headers: authHeaders(),
  })
  if (!r.ok) {
    const data = await r.json().catch(() => ({}))
    throw new Error(data?.detail || 'Failed to load saved predictions')
  }
  return await r.json()
}

/**
 * Get a single saved prediction by id.
 * @param {string} predictionId
 * @returns {Promise<Object>} Saved prediction object
 */
export async function getSavedPrediction(predictionId) {
  const r = await fetch(
    `${API_BASE_URL}/api/v1/me/saved-predictions/${encodeURIComponent(predictionId)}`,
    { headers: authHeaders() }
  )
  if (!r.ok) {
    const data = await r.json().catch(() => ({}))
    throw new Error(data?.detail || 'Failed to load saved prediction')
  }
  return await r.json()
}

/**
 * Save a custom prediction. Server enforces tier cap.
 * @param {Object} prediction - Prediction data to save
 * @param {string} prediction.city - City name
 * @param {string} prediction.property_type - 'house' or 'condo'
 * @param {Object} prediction.features - Input features snapshot
 * @param {number} prediction.predicted_price - Predicted price
 * @param {Object} prediction.prediction_response - Full response snapshot
 * @param {string} [prediction.name] - Optional user label
 * @param {string} [prediction.notes] - Optional user notes
 * @param {string} [prediction.address] - Resolved address
 * @param {Object} [prediction.coordinates] - { lat, lon }
 * @param {number} [prediction.confidence_lower]
 * @param {number} [prediction.confidence_upper]
 * @param {string} [prediction.model_version]
 * @returns {Promise<{ success: boolean, prediction?: Object, count?: number, cap?: number, error?: string }>}
 */
export async function savePrediction(prediction) {
  const r = await fetch(`${API_BASE_URL}/api/v1/me/saved-predictions`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(prediction),
  })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) {
    return {
      success: false,
      error: data?.detail || data?.error || 'Failed to save prediction',
    }
  }
  return {
    success: true,
    prediction: data?.prediction,
    count: data?.count,
    cap: data?.cap,
  }
}

/**
 * Update a saved prediction.
 * @param {string} predictionId
 * @param {Object} updates - { name?, notes?, features?, predicted_price?,
 *   confidence_lower?, confidence_upper?, prediction_response?, model_version? }
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function updateSavedPrediction(predictionId, updates) {
  const r = await fetch(
    `${API_BASE_URL}/api/v1/me/saved-predictions/${encodeURIComponent(predictionId)}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(updates),
    }
  )
  const data = await r.json().catch(() => ({}))
  if (!r.ok) {
    return {
      success: false,
      error: data?.detail || data?.error || 'Failed to update prediction',
    }
  }
  return { success: true }
}

/**
 * Delete a saved prediction by id.
 * @param {string} predictionId
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function deleteSavedPrediction(predictionId) {
  const r = await fetch(
    `${API_BASE_URL}/api/v1/me/saved-predictions/${encodeURIComponent(predictionId)}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  )
  const data = await r.json().catch(() => ({}))
  if (!r.ok) {
    return {
      success: false,
      error: data?.detail || data?.error || 'Failed to delete prediction',
    }
  }
  return { success: true }
}
